/**
 * Breadcrumb Generator Utility
 * Automatically generates breadcrumb trails based on current route
 */

import { BREADCRUMB_LABELS, ROUTE_CONFIGS, ROUTE_PATTERNS } from './breadcrumbConfig';

/**
 * Determines the dashboard path based on the route
 * Platform admin routes use /admin/platform, club admin routes use /admin/club
 */
const getDashboardPath = (pathname) => {
  if (pathname.startsWith('/admin/platform') || pathname.startsWith('/admin/clubs')) {
    return '/admin/platform';
  }
  return '/admin/club';
};

/**
 * Determines the dashboard label based on the route
 */
const getDashboardLabel = (pathname) => {
  if (pathname.startsWith('/admin/platform') || pathname.startsWith('/admin/clubs')) {
    return 'Platform Dashboard';
  }
  return 'Dashboard';
};

/**
 * Checks if a segment is a dynamic ID (UUID or numeric)
 */
const isDynamicId = (segment) => {
  // Check for UUID pattern
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(segment)) return true;
  
  // Check for numeric ID
  if (/^\d+$/.test(segment)) return true;
  
  return false;
};

/**
 * Generates breadcrumbs from route pathname
 *
 * @param {string} pathname - Current route pathname (e.g., '/admin/users/add')
 * @param {object} options - Optional configuration
 * @param {string} options.currentPageLabel - Override label for current page
 * @param {object} options.dynamicLabels - Labels for dynamic segments (e.g., { programId: 'U-12 Soccer' })
 * @returns {array} Array of breadcrumb items { label, path }
 */
export const generateBreadcrumbs = (pathname, options = {}) => {
  const { currentPageLabel, dynamicLabels = {} } = options;

  // Check if we have a specific configuration for this exact route
  if (ROUTE_CONFIGS[pathname]) {
    return ROUTE_CONFIGS[pathname].breadcrumbs;
  }

  // Check if pathname matches any pattern-based routes
  if (ROUTE_PATTERNS) {
    for (const routePattern of ROUTE_PATTERNS) {
      if (routePattern.pattern.test(pathname)) {
        return routePattern.generator(pathname, options);
      }
    }
  }
  
  // Parse the pathname into segments
  const segments = pathname.split('/').filter(Boolean);
  
  // If not an admin route, return empty
  if (segments[0] !== 'admin') {
    return [];
  }
  
  const breadcrumbs = [];
  let currentPath = '';
  
  // Build breadcrumbs from segments
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;

    // Skip 'admin' segment - we'll add dashboard instead
    if (segment === 'admin') {
      return;
    }

    // Skip 'club' segment when it appears after 'admin' (e.g., /admin/club/settings)
    // The dashboard is already added, so we don't need 'club' as a breadcrumb
    if (segment === 'club' && index === 1) {
      return;
    }

    // Add dashboard as first item (after 'admin')
    if (index === 1) {
      const dashboardPath = getDashboardPath(pathname);
      const dashboardLabel = getDashboardLabel(pathname);
      breadcrumbs.push({
        label: dashboardLabel,
        path: dashboardPath
      });
    }

    // Check if this is a dynamic ID segment
    if (isDynamicId(segment)) {
      // Use dynamic label if provided, otherwise skip
      if (dynamicLabels[segment]) {
        breadcrumbs.push({
          label: dynamicLabels[segment],
          path: currentPath
        });
      }
      return;
    }

    // Get label from configuration or capitalize segment
    const label = BREADCRUMB_LABELS[segment] ||
                  segment.split('-').map(word =>
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ');

    // Determine if this is the last segment (current page)
    const isLastSegment = index === segments.length - 1;

    // For the last segment, use currentPageLabel if provided, and set path to null
    if (isLastSegment) {
      breadcrumbs.push({
        label: currentPageLabel || label,
        path: null
      });
    } else {
      // For intermediate segments, include the path
      breadcrumbs.push({
        label: label,
        path: currentPath
      });
    }
  });
  
  return breadcrumbs;
};

/**
 * Helper function to generate breadcrumbs for edit pages
 * Handles the common pattern of /admin/section/:id/edit
 */
export const generateEditBreadcrumbs = (pathname, sectionLabel, sectionPath, itemLabel = 'Edit') => {
  const dashboardPath = getDashboardPath(pathname);
  const dashboardLabel = getDashboardLabel(pathname);
  
  return [
    { label: dashboardLabel, path: dashboardPath },
    { label: sectionLabel, path: sectionPath },
    { label: itemLabel, path: null }
  ];
};

/**
 * Helper function to generate breadcrumbs for nested resources
 * Handles patterns like /admin/programs/:programId/teams/:teamId
 */
export const generateNestedBreadcrumbs = (pathname, breadcrumbChain) => {
  const dashboardPath = getDashboardPath(pathname);
  const dashboardLabel = getDashboardLabel(pathname);
  
  return [
    { label: dashboardLabel, path: dashboardPath },
    ...breadcrumbChain
  ];
};

